/**
 * Marketing — WhatsApp + Email campaigns, segments, templates, tracking.
 * Contacts is the unified read-model over Prospects + Leads + Clients.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { celebrate } from "@/lib/celebrate";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Megaphone, Users, Layers, FileText, Send, Sparkles, Plus, Trash2, Edit3, Search as SearchIcon,
  Mail, MessageCircle, Play, BarChart3, Tag, CheckCircle2, AlertCircle, Zap, GitBranch, Wand2, Clock, StopCircle,
  Webhook, Copy, RefreshCw, Bot, Sprout, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { OverviewTab } from "./MarketingOverview";
import { ActiveThread } from "./WaInbox";
import CampaignEditDialog from "./CampaignEditDialog";
import { useSearchParams } from "react-router-dom";
import { apiErrorMessage } from "@/lib/apiError";


const TAB_KEYS = new Set(["overview", "contacts", "segments", "templates",
                                        "campaigns", "sequences", "scrape"]);

export default function Marketing() {
  const { activeWorkspaceId } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = TAB_KEYS.has(searchParams.get("tab") || "")
    ? searchParams.get("tab") : "overview";
  const [tab, setTab] = useState(initialTab);
  const [catalog, setCatalog] = useState(null);
  useEffect(() => { api.get("/marketing/catalog").then((r) => setCatalog(r.data)); }, []);
  // React to future URL changes (e.g. deep links from GTM apply flow)
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && TAB_KEYS.has(t) && t !== tab) setTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const changeTab = (v) => {
    setTab(v);
    const next = new URLSearchParams(searchParams);
    if (v === "overview") next.delete("tab"); else next.set("tab", v);
    setSearchParams(next, { replace: true });
  };
  return (
    <div className="space-y-6" data-testid="marketing-page">
      <PageHeader title="Marketing" subtitle="Unified contacts · Segments · Templates · WhatsApp & Email campaigns · AI-scrape autopilot." icon={Megaphone}
                       actions={
                         <a href="/app/marketing/wa-inbox"
                              className="inline-flex items-center gap-1 border border-primary text-primary hover:bg-primary hover:text-primary-foreground px-3 py-1.5 text-xs font-semibold uppercase tracking-widest"
                              data-testid="marketing-open-wa-inbox">
                           <MessageCircle className="w-3.5 h-3.5" /> WA Inbox
                         </a>
                       } />
      <Tabs value={tab} onValueChange={changeTab}>
        <TabsList className="rounded-none flex-wrap h-auto bg-transparent p-0 border-b border-border w-full justify-start gap-1">
          <TabsTrigger value="overview" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="marketing-tab-overview"><BarChart3 className="w-4 h-4 mr-2" />Overview</TabsTrigger>
          <TabsTrigger value="contacts" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="marketing-tab-contacts"><Users className="w-4 h-4 mr-2" />Contacts</TabsTrigger>
          <TabsTrigger value="segments" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="marketing-tab-segments"><Layers className="w-4 h-4 mr-2" />Segments</TabsTrigger>
          <TabsTrigger value="templates" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="marketing-tab-templates"><FileText className="w-4 h-4 mr-2" />Templates</TabsTrigger>
          <TabsTrigger value="campaigns" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="marketing-tab-campaigns"><Send className="w-4 h-4 mr-2" />Campaigns</TabsTrigger>
          <TabsTrigger value="sequences" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="marketing-tab-sequences"><GitBranch className="w-4 h-4 mr-2" />Sequences</TabsTrigger>
          <TabsTrigger value="scrape" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="marketing-tab-scrape"><Sparkles className="w-4 h-4 mr-2" />AI Autopilot</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-6"><OverviewTab wsId={activeWorkspaceId} /></TabsContent>
        <TabsContent value="contacts" className="mt-6"><ContactsTab wsId={activeWorkspaceId} /></TabsContent>
        <TabsContent value="segments" className="mt-6"><SegmentsTab wsId={activeWorkspaceId} catalog={catalog} /></TabsContent>
        <TabsContent value="templates" className="mt-6"><TemplatesTab wsId={activeWorkspaceId} catalog={catalog} /></TabsContent>
        <TabsContent value="campaigns" className="mt-6"><CampaignsTab wsId={activeWorkspaceId} /></TabsContent>
        <TabsContent value="sequences" className="mt-6"><SequencesTab wsId={activeWorkspaceId} catalog={catalog} /></TabsContent>
        <TabsContent value="scrape" className="mt-6"><ScrapeTab wsId={activeWorkspaceId} /></TabsContent>
      </Tabs>
    </div>
  );
}


// ─────────────── Contacts ───────────────
function ContactsTab({ wsId }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [sources, setSources] = useState("prospect,lead,client");
  const [assignee, setAssignee] = useState(""); // R52 · owner filter
  const [wsMembers, setWsMembers] = useState([]);
  const [selected, setSelected] = useState({});
  const [tagOpen, setTagOpen] = useState(false);
  const load = () => {
    const p = new URLSearchParams({ sources });
    if (q.trim()) p.set("q", q.trim());
    if (assignee) p.set("assignee_id", assignee);
    api.get(`/workspaces/${wsId}/marketing/contacts?${p.toString()}`).then((r) => setRows(r.data.items));
  };
  useEffect(() => { if (wsId) load(); /* eslint-disable-next-line */ }, [wsId, sources, assignee]);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/members`)
      .then(r => setWsMembers((r.data || []).filter(m => m.status !== "removed")))
      .catch(() => setWsMembers([]));
  }, [wsId]);
  const ids = Object.keys(selected).filter((k) => selected[k]);
  return (
    <div className="space-y-3" data-testid="contacts-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/50" />
          <Input className="pl-9 h-9 rounded-none" placeholder="Search across name, email, company, title, phone…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") load(); }} data-testid="contacts-search" />
        </div>
        <Select value={sources} onValueChange={setSources}>
          <SelectTrigger className="h-9 w-52 rounded-none" data-testid="contacts-sources-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="prospect,lead,client">All sources</SelectItem>
            <SelectItem value="prospect">Prospects only</SelectItem>
            <SelectItem value="lead">Leads only</SelectItem>
            <SelectItem value="client">Clients only</SelectItem>
          </SelectContent>
        </Select>
        <Select value={assignee || "_all"} onValueChange={(v) => setAssignee(v === "_all" ? "" : v)}>
          <SelectTrigger className="h-9 w-40 rounded-none" data-testid="contacts-owner-filter"><SelectValue placeholder="Owner" /></SelectTrigger>
          <SelectContent className="max-h-[280px]">
            <SelectItem value="_all">All owners</SelectItem>
            <SelectItem value="me">Assigned to me</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {wsMembers.map(m => (
              <SelectItem key={m.user_id} value={m.user_id}>{m.name || m.email || m.user_id}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {ids.length > 0 && <Button size="sm" className="rounded-none" onClick={() => setTagOpen(true)} data-testid="contacts-tag-btn"><Tag className="w-3 h-3 mr-1" />Tag {ids.length}</Button>}
      </div>
      {!rows ? <Card className="rounded-none"><CardContent className="p-6 text-sm text-foreground/60">Loading…</CardContent></Card>
        : rows.length === 0 ? <Card className="rounded-none border-dashed"><CardContent className="p-10 text-center text-sm text-foreground/60" data-testid="contacts-empty">No contacts. Import from Prospecting or add Leads/Clients in the CRM.</CardContent></Card>
        : (
          <div className="border border-border divide-y divide-border" data-testid="contacts-list">
            <div className="px-3 py-2 bg-foreground/5 text-[11px] font-semibold uppercase tracking-widest text-foreground/60 flex items-center gap-3">
              <span className="w-4" /><span className="flex-1">Contact</span><span className="w-24">Source</span><span className="w-40">Company</span><span className="w-40">Email/Phone</span>
            </div>
            {rows.slice(0, 300).map((c) => (
              <label key={c.id} className="px-3 py-2 flex items-center gap-3 text-sm hover:bg-foreground/5 cursor-pointer" data-testid={`contact-row-${c.id}`}>
                <input type="checkbox" checked={!!selected[c.id]} onChange={(e) => setSelected((s) => ({ ...s, [c.id]: e.target.checked }))} data-testid={`contact-check-${c.id}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{c.name || "(unnamed)"}</div>
                  <div className="text-[11px] text-foreground/55">{c.title}{c.title && c.city ? " · " : ""}{c.city}</div>
                  {c.tags && c.tags.length > 0 && <div className="flex gap-1 mt-1">{c.tags.slice(0, 4).map((t) => <Badge key={t} variant="outline" className="rounded-none text-[10px]">{t}</Badge>)}</div>}
                </div>
                <Badge variant="outline" className="rounded-none text-[10px] w-24 justify-center">{c.source}</Badge>
                <div className="w-40 truncate text-foreground/70">{c.company || "—"}</div>
                <div className="w-40 truncate text-foreground/70 font-mono text-[11px]">{c.email || c.phone || "—"}{c.opted_out_at && <Badge variant="outline" className="ml-1 rounded-none text-[10px] border-red-500/40 text-red-700">opt-out</Badge>}</div>
              </label>
            ))}
          </div>
        )}
      {tagOpen && <TagDialog wsId={wsId} ids={ids} onClose={() => setTagOpen(false)} onSaved={() => { setTagOpen(false); setSelected({}); load(); }} />}
    </div>
  );
}
function TagDialog({ wsId, ids, onClose, onSaved }) {
  const [add, setAdd] = useState("");
  const [remove, setRemove] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/workspaces/${wsId}/marketing/contacts/tag`, {
        contact_ids: ids,
        add_tags: add.split(",").map((s) => s.trim()).filter(Boolean),
        remove_tags: remove.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Tags updated");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="tag-dialog">
        <DialogHeader><DialogTitle>Tag {ids.length} contact(s)</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="label-eyebrow mb-1.5 inline-block">Add tags (comma-separated)</Label><Input value={add} onChange={(e) => setAdd(e.target.value)} placeholder="q1-outreach, saas, cold" className="h-9 rounded-none" data-testid="tag-add" /></div>
          <div><Label className="label-eyebrow mb-1.5 inline-block">Remove tags</Label><Input value={remove} onChange={(e) => setRemove(e.target.value)} placeholder="apollo-old" className="h-9 rounded-none" data-testid="tag-remove" /></div>
        </div>
        <DialogFooter><Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button><Button className="rounded-none" onClick={save} disabled={busy} data-testid="tag-save">{busy ? "…" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─────────────── Segments ───────────────
function SegmentsTab({ wsId, catalog }) {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [nurtureFor, setNurtureFor] = useState(null);
  const load = () => api.get(`/workspaces/${wsId}/marketing/segments`).then((r) => setRows(r.data));
  useEffect(() => { if (wsId) load(); /* eslint-disable-next-line */ }, [wsId]);
  return (
    <div className="space-y-3" data-testid="segments-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-foreground/70">Dynamic segments re-evaluate at send-time — always fresh. Wire a Sequence to <em>auto-nurture</em> inactive members.</p>
        <Button className="rounded-none" onClick={() => setEditing({})} data-testid="segment-new"><Plus className="w-4 h-4 mr-1" />New segment</Button>
      </div>
      {!rows ? <Card className="rounded-none"><CardContent className="p-6 text-sm text-foreground/60">Loading…</CardContent></Card>
        : rows.length === 0 ? <Card className="rounded-none border-dashed"><CardContent className="p-10 text-center text-sm text-foreground/60" data-testid="segments-empty">No segments yet. Build your first to power campaigns.</CardContent></Card>
        : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="segments-list">
            {rows.map((s) => {
              const nurture = s.auto_nurture || {};
              const nurtureOn = !!nurture.enabled;
              return (
                <Card key={s.id} className="rounded-none" data-testid={`segment-card-${s.id}`}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-center gap-2"><Layers className="w-4 h-4 text-primary" /><div className="font-semibold flex-1 truncate">{s.name}</div><Badge variant="outline" className="rounded-none">{s.count ?? "…"}</Badge></div>
                    {s.description && <p className="text-xs text-foreground/60 line-clamp-2">{s.description}</p>}
                    <div className="flex items-center gap-1 flex-wrap">{(s.sources || []).map((x) => <Badge key={x} variant="outline" className="rounded-none text-[10px]">{x}</Badge>)}</div>
                    {nurtureOn && (
                      <div className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest border border-emerald-400 text-emerald-800 bg-emerald-50 px-1.5 py-0.5" data-testid={`segment-nurture-chip-${s.id}`}>
                        <Sprout className="w-3 h-3" /> Auto-nurture · {nurture.delay_days || 3}d
                      </div>
                    )}
                    <div className="flex items-center gap-1 pt-2">
                      <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" onClick={() => setEditing(s)} data-testid={`segment-edit-${s.id}`}><Edit3 className="w-3 h-3" /></Button>
                      <Button size="sm" variant={nurtureOn ? "default" : "outline"} className={`rounded-none h-7 px-2 text-[10px] ${nurtureOn ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "border-emerald-400 text-emerald-800"}`} onClick={() => setNurtureFor(s)} data-testid={`segment-nurture-${s.id}`}><Sprout className="w-3 h-3 mr-1" />Nurture</Button>
                      <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-destructive" onClick={async () => { if (window.confirm(`Delete "${s.name}"?`)) { await api.delete(`/workspaces/${wsId}/marketing/segments/${s.id}`); toast.success("Deleted"); load(); } }} data-testid={`segment-delete-${s.id}`}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      {editing && <SegmentEditor wsId={wsId} seg={editing} catalog={catalog} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {nurtureFor && <AutoNurtureDialog wsId={wsId} seg={nurtureFor} onClose={() => setNurtureFor(null)} onSaved={() => { setNurtureFor(null); load(); }} />}
    </div>
  );
}

function AutoNurtureDialog({ wsId, seg, onClose, onSaved }) {
  const nurture = seg.auto_nurture || {};
  const [enabled, setEnabled] = useState(!!nurture.enabled);
  const [sequenceId, setSequenceId] = useState(nurture.sequence_id || "");
  const [delayDays, setDelayDays] = useState(nurture.delay_days ?? 3);
  const [sequences, setSequences] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/marketing/sequences`)
      .then((r) => setSequences(r.data || []))
      .catch(() => setSequences([]));
  }, [wsId]);
  const save = async () => {
    if (enabled && !sequenceId) { toast.error("Pick a sequence before enabling"); return; }
    setBusy(true);
    try {
      await api.put(`/workspaces/${wsId}/marketing/segments/${seg.id}/auto-nurture`, {
        enabled, sequence_id: sequenceId || null, delay_days: Number(delayDays) || 3,
      });
      toast.success(enabled ? "Auto-nurture wired" : "Auto-nurture disabled");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="segment-nurture-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Sprout className="w-4 h-4 text-emerald-600" />Auto-nurture · {seg.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="border border-emerald-300 bg-emerald-50/40 p-3 text-xs text-emerald-900">
            <b>How it works.</b> Every day at 08:15 UTC the drip scheduler auto-enrolls tagged
            members of this segment into the chosen sequence — but only after
            <span className="mx-1 font-mono">delay_days</span> of inactivity. Idempotent per
            contact × sequence, so re-enabling never sends duplicates.
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)}
                     data-testid="nurture-enabled" />
            Enable auto-nurture drip for this segment
          </label>
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Sequence *</Label>
            <Select value={sequenceId} onValueChange={setSequenceId}>
              <SelectTrigger className="h-9 rounded-none" data-testid="nurture-sequence"><SelectValue placeholder={sequences.length ? "Pick a sequence" : "No sequences yet — create one first"} /></SelectTrigger>
              <SelectContent>
                {sequences.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name} · {(s.steps || []).length} steps · {s.channel_default}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Inactivity delay (days) *</Label>
            <Input type="number" min="0" max="90" value={delayDays}
                     onChange={(e) => setDelayDays(e.target.value)}
                     className="h-9 rounded-none w-32" data-testid="nurture-delay" />
            <p className="text-[11px] text-foreground/60 mt-1">Wait this many days after the member&apos;s last update before enrolling.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy} data-testid="nurture-save">{busy ? "…" : (enabled ? "Wire & activate" : "Save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
function SegmentEditor({ wsId, seg, catalog, onClose, onSaved }) {
  const isNew = !seg?.id;
  const [name, setName] = useState(seg?.name || "");
  const [desc, setDesc] = useState(seg?.description || "");
  const [sources, setSources] = useState(seg?.sources || ["prospect", "lead", "client"]);
  const [conds, setConds] = useState(seg?.filter?.conditions || [{ field: "email", op: "has_email", value: null }]);
  const [join, setJoin] = useState(seg?.filter?.join || "and");
  const [busy, setBusy] = useState(false);
  const setCond = (i, k, v) => setConds((cs) => cs.map((c, idx) => idx === i ? { ...c, [k]: v } : c));
  const save = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const payload = { name, description: desc, sources, filter: { conditions: conds, join } };
      if (isNew) await api.post(`/workspaces/${wsId}/marketing/segments`, payload);
      else await api.patch(`/workspaces/${wsId}/marketing/segments/${seg.id}`, payload);
      toast.success(isNew ? "Segment created" : "Updated");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="segment-editor">
        <DialogHeader><DialogTitle>{isNew ? "New segment" : `Edit ${seg.name}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="label-eyebrow mb-1.5 inline-block">Name *</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 rounded-none" data-testid="segment-name" /></div>
          <div><Label className="label-eyebrow mb-1.5 inline-block">Description</Label><Input value={desc} onChange={(e) => setDesc(e.target.value)} className="h-9 rounded-none" data-testid="segment-desc" /></div>
          <div className="flex items-center gap-3 text-xs">
            {["prospect", "lead", "client"].map((s) => (
              <label key={s} className="flex items-center gap-1"><input type="checkbox" checked={sources.includes(s)} onChange={(e) => setSources(e.target.checked ? [...sources, s] : sources.filter((x) => x !== s))} data-testid={`segment-source-${s}`} />{s}</label>
            ))}
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="label-eyebrow">Conditions ({join.toUpperCase()})</Label>
              <div className="flex items-center gap-1"><Button size="sm" variant={join === "and" ? "default" : "outline"} className="rounded-none h-6 text-[10px]" onClick={() => setJoin("and")}>AND</Button><Button size="sm" variant={join === "or" ? "default" : "outline"} className="rounded-none h-6 text-[10px]" onClick={() => setJoin("or")}>OR</Button></div>
            </div>
            <div className="space-y-2" data-testid="segment-conditions">
              {conds.map((c, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Select value={c.field} onValueChange={(v) => setCond(i, "field", v)}><SelectTrigger className="h-8 w-40 rounded-none"><SelectValue /></SelectTrigger><SelectContent>{(catalog?.filter_fields || []).map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent></Select>
                  <Select value={c.op} onValueChange={(v) => setCond(i, "op", v)}><SelectTrigger className="h-8 w-36 rounded-none"><SelectValue /></SelectTrigger><SelectContent>{(catalog?.filter_ops || []).map((o) => <SelectItem key={o} value={o}>{o.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select>
                  <Input value={c.value ?? ""} onChange={(e) => setCond(i, "value", e.target.value)} className="h-8 flex-1 rounded-none" placeholder="value" />
                  <Button size="sm" variant="ghost" className="rounded-none h-8 px-2 text-destructive" onClick={() => setConds(conds.filter((_, idx) => idx !== i))}><Trash2 className="w-3 h-3" /></Button>
                </div>
              ))}
              <Button size="sm" variant="outline" className="rounded-none" onClick={() => setConds([...conds, { field: "name", op: "contains", value: "" }])} data-testid="segment-add-cond"><Plus className="w-3 h-3 mr-1" />Add condition</Button>
            </div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button><Button className="rounded-none" onClick={save} disabled={busy} data-testid="segment-save">{busy ? "…" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─────────────── Templates ───────────────
function TemplatesTab({ wsId, catalog }) {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get(`/workspaces/${wsId}/marketing/templates`).then((r) => setRows(r.data));
  useEffect(() => { if (wsId) load(); /* eslint-disable-next-line */ }, [wsId]);
  return (
    <div className="space-y-3" data-testid="templates-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-foreground/70">Merge tags: {(catalog?.merge_tags || []).map((t) => <code key={t} className="mx-0.5 text-xs">{`{${t}}`}</code>)}</p>
        <Button className="rounded-none" onClick={() => setEditing({})} data-testid="template-new"><Plus className="w-4 h-4 mr-1" />New template</Button>
      </div>
      {!rows ? <Card className="rounded-none"><CardContent className="p-6 text-sm text-foreground/60">Loading…</CardContent></Card>
        : rows.length === 0 ? <Card className="rounded-none border-dashed"><CardContent className="p-10 text-center text-sm text-foreground/60" data-testid="templates-empty">No templates yet.</CardContent></Card>
        : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="templates-list">
            {rows.map((t) => (
              <Card key={t.id} className="rounded-none" data-testid={`template-card-${t.id}`}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    {t.channel === "email" ? <Mail className="w-4 h-4 text-primary" /> : <MessageCircle className="w-4 h-4 text-emerald-600" />}
                    <div className="font-semibold flex-1 truncate">{t.name}</div>
                    <Badge variant="outline" className="rounded-none text-[10px]">{t.channel}</Badge>
                  </div>
                  {t.subject && <div className="text-xs text-foreground/70 truncate"><strong>Subject:</strong> {t.subject}</div>}
                  <div className="text-xs text-foreground/60 line-clamp-3 whitespace-pre-wrap">{t.body}</div>
                  <div className="flex items-center gap-1 pt-1">
                    <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" onClick={() => setEditing(t)} data-testid={`template-edit-${t.id}`}><Edit3 className="w-3 h-3" /></Button>
                    <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-destructive" onClick={async () => { if (window.confirm(`Delete "${t.name}"?`)) { await api.delete(`/workspaces/${wsId}/marketing/templates/${t.id}`); toast.success("Deleted"); load(); } }} data-testid={`template-delete-${t.id}`}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      {editing && <TemplateEditor wsId={wsId} tmpl={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
function TemplateEditor({ wsId, tmpl, onClose, onSaved }) {
  const isNew = !tmpl?.id;
  const [name, setName] = useState(tmpl?.name || "");
  const [channel, setChannel] = useState(tmpl?.channel || "email");
  const [subject, setSubject] = useState(tmpl?.subject || "");
  const [body, setBody] = useState(tmpl?.body || "");
  const [waSid, setWaSid] = useState(tmpl?.wa_template_sid || "");
  const [busy, setBusy] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const save = async () => {
    if (!name.trim() || !body.trim()) { toast.error("Name and body are required"); return; }
    setBusy(true);
    try {
      const payload = { name, channel, subject: subject || null, body, wa_template_sid: waSid || null, wa_variables: [] };
      if (isNew) await api.post(`/workspaces/${wsId}/marketing/templates`, payload);
      else await api.patch(`/workspaces/${wsId}/marketing/templates/${tmpl.id}`, payload);
      toast.success(isNew ? "Created" : "Updated");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="template-editor">
        <DialogHeader><DialogTitle>{isNew ? "New template" : `Edit ${tmpl.name}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-eyebrow mb-1.5 inline-block">Name *</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 rounded-none" data-testid="template-name" /></div>
            <div><Label className="label-eyebrow mb-1.5 inline-block">Channel *</Label><Select value={channel} onValueChange={setChannel}><SelectTrigger className="h-9 rounded-none" data-testid="template-channel"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="email">Email</SelectItem><SelectItem value="whatsapp">WhatsApp</SelectItem></SelectContent></Select></div>
          </div>
          {channel === "email" && <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="label-eyebrow inline-block">Subject</Label>
              <SubjectOptimizerButton
                wsId={wsId}
                baseSubject={subject}
                onPick={(s) => setSubject(s)}
                testid="template-subject-optimize"
              />
            </div>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Quick intro, {first_name}" className="h-9 rounded-none" data-testid="template-subject" />
          </div>}
          {channel === "whatsapp" && <div><Label className="label-eyebrow mb-1.5 inline-block">Approved template SID / name</Label><Input value={waSid} onChange={(e) => setWaSid(e.target.value)} placeholder="HXxxxxxxxxxxxx (Twilio) or interakt_template_name" className="h-9 rounded-none font-mono text-xs" data-testid="template-wa-sid" /></div>}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="label-eyebrow inline-block">Body * (supports {`{merge_tags}`})</Label>
              <Button size="sm" variant="outline" className="rounded-none h-7 text-xs border-primary/40 text-primary" onClick={() => setAiOpen(true)} data-testid="template-ai-generate"><Wand2 className="w-3 h-3 mr-1" />AI Generate</Button>
            </div>
            <Textarea rows={8} value={body} onChange={(e) => setBody(e.target.value)} className="rounded-none font-mono text-xs" placeholder="Hi {first_name}, saw {company} — would love to chat about..." data-testid="template-body" />
          </div>
        </div>
        <DialogFooter><Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button><Button className="rounded-none" onClick={save} disabled={busy} data-testid="template-save">{busy ? "…" : "Save"}</Button></DialogFooter>
        {aiOpen && (
          <AiCopywriterDialog
            wsId={wsId}
            channel={channel}
            onClose={() => setAiOpen(false)}
            onUse={(v) => { if (v.subject) setSubject(v.subject); setBody(v.body); setAiOpen(false); toast.success("Draft applied"); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}


// ─────────────── AI Copywriter Dialog ───────────────
function AiCopywriterDialog({ wsId, channel, onClose, onUse }) {
  const [goal, setGoal] = useState("");
  const [tone, setTone] = useState("friendly");
  const [audience, setAudience] = useState("");
  const [keyPoints, setKeyPoints] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [busy, setBusy] = useState(false);
  const [variants, setVariants] = useState([]);
  const gen = async () => {
    if (goal.trim().length < 3) { toast.error("Goal is required"); return; }
    setBusy(true); setVariants([]);
    try {
      const r = await api.post(`/workspaces/${wsId}/marketing/ai/copywriter`, {
        channel, goal, tone, audience: audience || null,
        company_name: companyName || null,
        key_points: keyPoints.split("\n").map(s => s.trim()).filter(Boolean),
        variants: 3,
      });
      setVariants(r.data.variants || []);
      if (!r.data.variants?.length) toast.error("AI returned no variants");
    } catch (e) { toast.error(apiErrorMessage(e, "AI failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ai-copywriter-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Wand2 className="w-4 h-4 text-primary" />AI Copywriter · {channel === "email" ? "Email" : "WhatsApp"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="label-eyebrow mb-1.5 inline-block">Goal / call-to-action *</Label><Input value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="Book a 15-min demo call" className="h-9 rounded-none" data-testid="ai-goal" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-eyebrow mb-1.5 inline-block">Tone</Label><Select value={tone} onValueChange={setTone}><SelectTrigger className="h-9 rounded-none" data-testid="ai-tone"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="friendly">Friendly</SelectItem><SelectItem value="professional">Professional</SelectItem><SelectItem value="urgent">Urgent</SelectItem><SelectItem value="playful">Playful</SelectItem><SelectItem value="direct">Direct</SelectItem></SelectContent></Select></div>
            <div><Label className="label-eyebrow mb-1.5 inline-block">Sender company</Label><Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Acquilix" className="h-9 rounded-none" data-testid="ai-company" /></div>
          </div>
          <div><Label className="label-eyebrow mb-1.5 inline-block">Audience</Label><Input value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="SaaS founders in India, 11-50 employees" className="h-9 rounded-none" data-testid="ai-audience" /></div>
          <div><Label className="label-eyebrow mb-1.5 inline-block">Key points (one per line)</Label><Textarea rows={3} value={keyPoints} onChange={(e) => setKeyPoints(e.target.value)} placeholder={"we saved Zeta 40% on ops\nfree onboarding included"} className="rounded-none text-xs" data-testid="ai-key-points" /></div>
          <Button className="rounded-none w-full" onClick={gen} disabled={busy} data-testid="ai-generate-btn"><Wand2 className="w-3 h-3 mr-1" />{busy ? "Generating 3 variants…" : "Generate 3 variants"}</Button>
          {variants.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 pt-2" data-testid="ai-variants">
              {variants.map((v, i) => (
                <Card key={i} className="rounded-none" data-testid={`ai-variant-${i}`}>
                  <CardContent className="p-3 space-y-2">
                    <div className="text-[10px] label-eyebrow">Variant {i + 1}</div>
                    {v.subject && <div className="text-xs font-semibold border-b border-border pb-1.5"><span className="text-foreground/60">Subject:</span> {v.subject}</div>}
                    <div className="text-xs whitespace-pre-wrap max-h-56 overflow-y-auto" dangerouslySetInnerHTML={channel === "email" ? { __html: v.body } : undefined}>
                      {channel === "whatsapp" ? v.body : null}
                    </div>
                    <Button size="sm" className="rounded-none w-full" onClick={() => onUse(v)} data-testid={`ai-use-${i}`}>Use this draft</Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
        <DialogFooter><Button variant="outline" className="rounded-none" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─────────────── Campaigns ───────────────
function CampaignsTab({ wsId }) {
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = () => api.get(`/workspaces/${wsId}/marketing/campaigns`).then((r) => setRows(r.data));
  useEffect(() => { if (wsId) load(); /* eslint-disable-next-line */ }, [wsId]);
  return (
    <div className="space-y-3" data-testid="campaigns-tab">
      <WarmupCard wsId={wsId} />
      <BlacklistCheckCard wsId={wsId} />
      <SubjectLeaderboardCard wsId={wsId} />
      <div className="flex items-center justify-between">
        <p className="text-sm text-foreground/70">One-shot campaigns · sequences ship next iteration.</p>
        <Button className="rounded-none" onClick={() => setCreating(true)} data-testid="campaign-new"><Plus className="w-4 h-4 mr-1" />New campaign</Button>
      </div>
      {!rows ? <Card className="rounded-none"><CardContent className="p-6 text-sm text-foreground/60">Loading…</CardContent></Card>
        : rows.length === 0 ? <Card className="rounded-none border-dashed"><CardContent className="p-10 text-center text-sm text-foreground/60" data-testid="campaigns-empty">No campaigns. Create your first.</CardContent></Card>
        : (
          <div className="space-y-2" data-testid="campaigns-list">
            {rows.map((c) => <CampaignRow key={c.id} wsId={wsId} c={c} onChange={load} />)}
          </div>
        )}
      {creating && <CampaignEditor wsId={wsId} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}
function CampaignRow({ wsId, c, onChange }) {
  const [busy, setBusy] = useState(false);
  const [ab, setAb] = useState(null);
  const [showRepliers, setShowRepliers] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const send = async () => {
    if (!window.confirm(`Send "${c.name}" now?`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/marketing/campaigns/${c.id}/send-now`);
      celebrate("campaign_sent", { name: c.name || "Campaign" });
      const s = r.data.stats;
      toast.success(`Sent ${s.sent}${s.scheduled ? ` · ${s.scheduled} scheduled` : ""}${s.deferred ? ` · ${s.deferred} deferred (warm-up cap)` : ""} / ${r.data.recipients} recipients`);
      onChange();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };
  const loadAb = async () => {
    if (ab) { setAb(null); return; }
    try { const r = await api.get(`/workspaces/${wsId}/marketing/campaigns/${c.id}/ab-results`); setAb(r.data); }
    catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };
  return (
    <div className="border border-border" data-testid={`campaign-row-${c.id}`}>
      <div className="p-4 flex items-center gap-3">
      {c.channel === "email" ? <Mail className="w-4 h-4 text-primary" /> : <MessageCircle className="w-4 h-4 text-emerald-600" />}
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate">{c.name}</div>
        <div className="text-xs text-foreground/60 flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="rounded-none text-[10px]">{c.status}</Badge>
          <Badge variant="outline" className="rounded-none text-[10px]">{c.provider}</Badge>
          <span>· sent {c.stats?.sent || 0}</span>
          <span>· opened {c.stats?.opened || 0}</span>
          <span>· clicked {c.stats?.clicked || 0}</span>
          <span>· failed {c.stats?.failed || 0}</span>
          {(c.stats?.scheduled || 0) + (c.stats?.deferred || 0) > 0 && <span>· queued {(c.stats?.scheduled || 0) + (c.stats?.deferred || 0)}</span>}
          {c.ab_test?.enabled && <Badge variant="outline" className="rounded-none text-[10px] border-violet-500/40 text-violet-700 cursor-pointer" onClick={loadAb} data-testid={`campaign-ab-badge-${c.id}`}>A/B {c.ab_test.split_pct}/{100 - c.ab_test.split_pct}</Badge>}
          {c.send_time_optimization && <Badge variant="outline" className="rounded-none text-[10px] border-sky-500/40 text-sky-700">STO</Badge>}
          {c.dry_run && <Badge variant="outline" className="rounded-none text-[10px] border-amber-500/40 text-amber-700">DRY RUN</Badge>}
        </div>
      </div>
      {c.status === "draft" && (
        <Button size="sm" className="rounded-none" onClick={send} disabled={busy} data-testid={`campaign-send-${c.id}`}><Play className="w-3 h-3 mr-1" />{busy ? "Sending…" : "Send now"}</Button>
      )}
      {c.status !== "draft" && (c.stats?.replied || 0) > 0 && (
        <Button size="sm" variant="outline" className="rounded-none" onClick={() => setShowRepliers(true)} data-testid={`campaign-repliers-btn-${c.id}`}>
          <MessageCircle className="w-3 h-3 mr-1" />Repliers ({c.stats?.replied || 0})
        </Button>
      )}
      <Button size="sm" variant="ghost" className="rounded-none" onClick={() => setShowEdit(true)} title="Edit campaign" data-testid={`campaign-edit-${c.id}`}><Pencil className="w-3 h-3" /></Button>
      <Button size="sm" variant="ghost" className="rounded-none text-destructive" onClick={async () => { if (window.confirm("Delete?")) { await api.delete(`/workspaces/${wsId}/marketing/campaigns/${c.id}`); toast.success("Deleted"); onChange(); } }} data-testid={`campaign-delete-${c.id}`}><Trash2 className="w-3 h-3" /></Button>
      </div>
      {showRepliers && (
        <RepliersDialog wsId={wsId} campaign={c}
                              onClose={() => setShowRepliers(false)} />
      )}
      {showEdit && (
        <CampaignEditDialog wsId={wsId} campaign={c}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); onChange(); }} />
      )}
      {ab && (
        <div className="border-t border-border px-4 py-3 bg-violet-500/5" data-testid={`campaign-ab-results-${c.id}`}>
          {ab.promoted_variant && <p className="text-xs text-emerald-700 font-semibold mb-2" data-testid={`campaign-ab-promoted-${c.id}`}>⚡ Auto-promoted: remaining queued sends switched to Variant {ab.promoted_variant}</p>}
          <div className="grid grid-cols-2 gap-3">
          {["A", "B"].map((v) => (
            <div key={v} className={`border p-2.5 text-xs ${ab.winner === v ? "border-emerald-500 bg-emerald-500/5" : "border-border bg-background"}`}>
              <p className="font-semibold">Variant {v} {ab.winner === v && <span className="text-emerald-600">🏆 winner ({ab.winner_metric})</span>}</p>
              <p className="text-foreground/60 mt-1">sent {ab.variants[v].sent} · opened {ab.variants[v].opened} ({ab.variants[v].open_rate}%) · clicked {ab.variants[v].clicked} ({ab.variants[v].click_rate}%)</p>
            </div>
          ))}
          </div>
        </div>
      )}
    </div>
  );
}


// Iter 194 · Repliers list + "add to segment" dialog
function RepliersDialog({ wsId, campaign, onClose }) {
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [segments, setSegments] = useState([]);
  const [pickedSeg, setPickedSeg] = useState("");
  const [newSegName, setNewSegName] = useState("");
  const [busy, setBusy] = useState(false);
  // Iter 195 — mini-thread inside the dialog
  const [openThread, setOpenThread] = useState(null);

  useEffect(() => {
    api.get(`/workspaces/${wsId}/marketing/campaigns/${campaign.id}/repliers`)
      .then((r) => { setRows(r.data.rows || []); setTotal(r.data.total || 0); })
      .catch(() => setRows([]));
    api.get(`/workspaces/${wsId}/marketing/segments`)
      .then((r) => setSegments((r.data || []).filter((s) => s.kind === "tag")))
      .catch(() => setSegments([]));
  }, [wsId, campaign.id]);

  const apply = async () => {
    if (!pickedSeg && !newSegName.trim()) {
      toast.error("Pick a segment or name a new one");
      return;
    }
    setBusy(true);
    try {
      const body = pickedSeg
        ? { segment_id: pickedSeg }
        : { new_segment_name: newSegName.trim() };
      const r = await api.post(
        `/workspaces/${wsId}/marketing/campaigns/${campaign.id}/repliers/add-to-segment`,
        body);
      toast.success(`Tagged ${r.data.tagged} replier${r.data.tagged === 1 ? "" : "s"} with ${r.data.tag}`);
      onClose();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto"
                          data-testid="repliers-dialog">
        <DialogHeader>
          <DialogTitle>Repliers to &quot;{campaign.name}&quot; · {total}</DialogTitle>
        </DialogHeader>

        {rows === null && <div className="text-xs text-muted-foreground p-4">Loading…</div>}
        {rows !== null && rows.length === 0 && (
          <div className="text-xs text-muted-foreground p-4 text-center border border-dashed border-border">
            Nobody replied yet.
          </div>
        )}
        {rows !== null && rows.length > 0 && (
          <div className="border border-border max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/20">
                <tr>
                  <th className="p-2 text-left uppercase tracking-widest text-[10px]">Name</th>
                  <th className="p-2 text-left uppercase tracking-widest text-[10px]">Contact</th>
                  <th className="p-2 text-left uppercase tracking-widest text-[10px]">Last reply</th>
                  <th className="p-2 text-right uppercase tracking-widest text-[10px]">#</th>
                  <th className="p-2 text-right uppercase tracking-widest text-[10px]">Chat</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.source}-${r.source_id}`} className="border-t border-border">
                    <td className="p-2 font-medium">{r.name}</td>
                    <td className="p-2 text-muted-foreground text-[11px]">
                      {r.email || r.phone || "—"}
                    </td>
                    <td className="p-2 text-muted-foreground text-[11px] max-w-xs truncate"
                            title={r.last_reply_text || ""}>
                      {r.last_reply_text || "—"}
                    </td>
                    <td className="p-2 text-right font-mono">{r.reply_count}</td>
                    <td className="p-2 text-right">
                      <button type="button"
                                  onClick={() => setOpenThread(`${r.source}:${r.source_id}`)}
                                  className="inline-flex items-center gap-1 text-primary hover:underline text-[11px]"
                                  data-testid={`repliers-open-thread-${r.source_id}`}>
                        <MessageCircle className="w-3 h-3" /> Chat
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {rows !== null && rows.length > 0 && openThread && (
          <div className="border border-primary/40 bg-primary/5 p-2 space-y-1"
                    data-testid="repliers-mini-thread">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-primary">
                Conversation
              </div>
              <button type="button" onClick={() => setOpenThread(null)}
                          className="text-[10px] text-muted-foreground hover:text-primary underline">
                Close
              </button>
            </div>
            <div className="border border-border bg-card">
              <ActiveThread wsId={wsId} contactRef={openThread} embedded />
            </div>
          </div>
        )}

        {rows !== null && rows.length > 0 && (
          <div className="border border-border p-3 space-y-2 bg-muted/10">
            <div className="text-[10px] uppercase tracking-widest font-semibold">
              Add all repliers to a segment
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px]">Existing tag segment</Label>
                <Select value={pickedSeg} onValueChange={(v) => { setPickedSeg(v); setNewSegName(""); }}>
                  <SelectTrigger className="h-8 rounded-none" data-testid="repliers-segment-picker">
                    <SelectValue placeholder="Pick a segment" />
                  </SelectTrigger>
                  <SelectContent>
                    {segments.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px]">Or new segment name</Label>
                <Input value={newSegName}
                          onChange={(e) => { setNewSegName(e.target.value); setPickedSeg(""); }}
                          className="h-8 rounded-none text-xs"
                          placeholder="e.g. Q1-Promo Repliers"
                          data-testid="repliers-new-segment-name" />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Close</Button>
          {rows !== null && rows.length > 0 && (
            <Button className="rounded-none" onClick={apply} disabled={busy}
                      data-testid="repliers-apply-btn">
              {busy ? "Tagging…" : `Add ${rows.length} to segment`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WarmupCard({ wsId }) {
  const [w, setW] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get(`/workspaces/${wsId}/marketing/warmup`).then((r) => setW(r.data)).catch(() => {});
  useEffect(() => { if (wsId) load(); /* eslint-disable-next-line */ }, [wsId]);
  if (!w) return null;
  const save = async (patch) => {
    setSaving(true);
    try { const r = await api.put(`/workspaces/${wsId}/marketing/warmup`, patch); setW(r.data); toast.success("Warm-up updated"); }
    catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setSaving(false); }
  };
  const statusBadge = () => {
    if (w.status === "complete") {
      return (
        <span className="inline-flex items-center gap-1 border border-emerald-500/40 text-emerald-700 px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold" data-testid="warmup-status-badge">
          ✓ Complete
          {w.completed_at && (
            <span className="text-emerald-600/70 font-normal normal-case text-[9px]">
              · since {new Date(w.completed_at).toLocaleDateString()}
            </span>
          )}
        </span>
      );
    }
    if (w.status === "warming") {
      return (
        <span className="inline-flex items-center gap-1 border border-amber-500/40 text-amber-700 px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold" data-testid="warmup-status-badge">
          ● Warming · {w.progress_pct}%
          {w.days_until_complete != null && (
            <span className="text-amber-600/70 font-normal normal-case text-[9px]">
              · ~{w.days_until_complete}d left
            </span>
          )}
        </span>
      );
    }
    return null;
  };
  return (
    <div className="border border-border p-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs" data-testid="warmup-card">
      <label className="flex items-center gap-2 cursor-pointer font-semibold">
        <input type="checkbox" checked={w.enabled} disabled={saving} onChange={(e) => save({ enabled: e.target.checked })} data-testid="warmup-enabled" />
        Deliverability warm-up
      </label>
      {statusBadge()}
      {w.enabled ? (
        <>
          {/* Progress bar (Iter 200d) */}
          {w.status === "warming" && (
            <span className="inline-flex items-center gap-1.5 min-w-[120px]" data-testid="warmup-progress">
              <span className="w-24 h-1.5 bg-muted rounded-full overflow-hidden">
                <span className="block h-full bg-amber-500 transition-all"
                      style={{ width: `${w.progress_pct}%` }} />
              </span>
            </span>
          )}
          <span data-testid="warmup-status">Day <b>{w.day}</b> · today&apos;s cap <b>{w.today_cap ?? "∞"}</b> · sent <b>{w.sent_today}</b> · remaining <b className={w.remaining_today === 0 ? "text-red-600" : "text-emerald-600"}>{w.remaining_today ?? "∞"}</b>{w.pending_scheduled > 0 && <> · queued <b>{w.pending_scheduled}</b></>}</span>
          <span className="flex items-center gap-1">start <Input type="number" value={w.start_cap} onChange={(e) => setW({ ...w, start_cap: Number(e.target.value) })} onBlur={() => save({ start_cap: w.start_cap })} className="h-6 w-16 rounded-none text-xs" data-testid="warmup-start-cap" /></span>
          <span className="flex items-center gap-1">+<Input type="number" value={w.growth_pct} onChange={(e) => setW({ ...w, growth_pct: Number(e.target.value) })} onBlur={() => save({ growth_pct: w.growth_pct })} className="h-6 w-14 rounded-none text-xs" data-testid="warmup-growth" />%/day</span>
          <span className="flex items-center gap-1">max <Input type="number" value={w.max_cap} onChange={(e) => setW({ ...w, max_cap: Number(e.target.value) })} onBlur={() => save({ max_cap: w.max_cap })} className="h-6 w-20 rounded-none text-xs" data-testid="warmup-max-cap" /></span>
          <button className="text-foreground/50 hover:text-foreground underline" onClick={() => save({ restart: true })} data-testid="warmup-restart">restart day 1</button>
        </>
      ) : <span className="text-foreground/50">Ramps daily email volume gradually to protect sender reputation. Overflow is auto-queued for the next day.</span>}
    </div>
  );
}

function BlacklistCheckCard({ wsId }) {
  const [target, setTarget] = useState("");
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const runCheck = async () => {
    const t = target.trim();
    if (!t) { toast.error("Enter a domain or IP"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/marketing/blacklist-check`, { target: t });
      setReport(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Blacklist check failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-border p-3 text-xs space-y-2" data-testid="blacklist-check-card">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold">Blacklist / RBL check</span>
        <span className="text-foreground/50">Multi-RBL lookup across Spamhaus, SpamCop, Barracuda, SORBS, PSBL (IP) + DBL / SURBL / URIBL (domain).</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          placeholder="yourdomain.com or 1.2.3.4"
          className="h-8 w-72 rounded-none text-xs"
          onKeyDown={(e) => { if (e.key === "Enter") runCheck(); }}
          data-testid="blacklist-input"
        />
        <Button className="rounded-none h-8" onClick={runCheck} disabled={busy}
                 data-testid="blacklist-run">
          {busy ? "Checking…" : "Check"}
        </Button>
        {report && (
          <span className="text-[10px] text-foreground/50">
            {report.cached ? "cached" : "fresh"} · {report.checked_at?.slice(0, 16).replace("T", " ")}
          </span>
        )}
      </div>
      {report && (
        <div data-testid="blacklist-report">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold ${
              report.clean ? "bg-emerald-100 text-emerald-800 border border-emerald-500/40"
                : report.listed_count > 0 ? "bg-red-100 text-red-800 border border-red-500/40"
                : "bg-amber-100 text-amber-800 border border-amber-500/40"
            }`} data-testid="blacklist-overall">
              {report.clean ? "✓ Clean" : report.listed_count > 0 ? `✗ Listed on ${report.listed_count}` : "⚠ No result"}
            </span>
            <span className="text-foreground/70">
              {report.target} {report.ip && report.type === "domain" && <span className="text-foreground/50">→ {report.ip}</span>}
            </span>
            <span className="text-foreground/50">· {report.checked_count} providers checked</span>
          </div>
          {report.error && (
            <div className="text-red-600 text-[11px] mb-2" data-testid="blacklist-error">{report.error}</div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
            {(report.checks || []).map((c, i) => (
              <div
                key={`${c.provider}-${i}`}
                className={`flex items-center gap-1.5 px-2 py-1 border ${
                  c.listed ? "border-red-500/60 bg-red-50"
                    : c.error ? "border-amber-500/40 bg-amber-50"
                    : "border-emerald-500/40 bg-emerald-50"
                }`}
                data-testid={`blacklist-row-${c.provider.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <span className={c.listed ? "text-red-700" : c.error ? "text-amber-700" : "text-emerald-700"}>
                  {c.listed ? "✗" : c.error ? "⚠" : "✓"}
                </span>
                <span className="font-semibold flex-1">{c.provider}</span>
                <span className="text-[9px] text-foreground/50 uppercase">{c.kind}</span>
                {c.reason && (
                  <span className="text-[10px] text-foreground/60 truncate max-w-[160px]" title={c.reason}>
                    {c.reason}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SubjectLeaderboardCard({ wsId }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/marketing/subject-leaderboard?days=${days}&limit=25`)
      .then((r) => setData(r.data))
      .catch(() => setData({ rows: [], avg_open_rate: 0, total_sent: 0 }));
  }, [wsId, days]);
  if (!data) return null;
  const rowsToShow = expanded ? data.rows : data.rows.slice(0, 5);
  return (
    <div className="border border-border p-3 text-xs space-y-2" data-testid="subject-leaderboard-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="font-semibold">Subject-line leaderboard</span>
          <span className="text-foreground/50">
            Last {days}d · {data.total_sent} sent · avg open <b>{data.avg_open_rate}%</b>
          </span>
        </div>
        <div className="flex items-center gap-1">
          {[7, 30, 90].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setDays(n)}
              className={`px-1.5 py-0.5 text-[10px] font-mono border ${days === n ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
              data-testid={`subject-lb-days-${n}`}
            >{n}d</button>
          ))}
        </div>
      </div>
      {data.rows.length === 0 ? (
        <div className="text-foreground/50 italic py-2" data-testid="subject-lb-empty">
          No sent emails with subjects yet — as campaigns fire, subjects will
          rank here by open rate.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-12 gap-2 border-b border-border py-1 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
            <div className="col-span-6">Subject</div>
            <div className="col-span-2 text-right">Sent</div>
            <div className="col-span-2 text-right">Open %</div>
            <div className="col-span-2 text-right">Click %</div>
          </div>
          {rowsToShow.map((r, i) => (
            <div key={`${r.subject}-${i}`} className="grid grid-cols-12 gap-2 py-1 border-b border-border/50 hover:bg-muted/20" data-testid={`subject-lb-row-${i}`}>
              <div className="col-span-6 truncate" title={r.subject}>{r.subject}</div>
              <div className="col-span-2 text-right font-mono">{r.sent}</div>
              <div className="col-span-2 text-right font-mono">
                <span className={r.open_rate >= (data.avg_open_rate || 0) ? "text-emerald-600" : "text-muted-foreground"}>
                  {r.open_rate}%
                </span>
              </div>
              <div className="col-span-2 text-right font-mono">{r.click_rate}%</div>
            </div>
          ))}
          {data.rows.length > 5 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-primary text-[11px] hover:underline"
              data-testid="subject-lb-toggle"
            >
              {expanded ? "Show top 5" : `Show all ${data.rows.length}`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

function SubjectOptimizerButton({ wsId, baseSubject, audience, goal,
                                    onPick, testid = "subject-optimizer-btn" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [variants, setVariants] = useState([]);
  const [tone, setTone] = useState("friendly");
  const [numVariants, setNumVariants] = useState(4);

  const run = async () => {
    if (!(baseSubject || "").trim()) {
      toast.error("Enter a base subject first");
      return;
    }
    setBusy(true);
    setVariants([]);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/marketing/subject-optimizer`,
        { base_subject: baseSubject, audience, goal, tone,
          variants: numVariants });
      setVariants(data.variants || []);
      if ((data.variants || []).length === 0) toast.error("AI returned no variants");
    } catch (e) {
      toast.error(apiErrorMessage(e, "AI optimizer failed"));
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => { setOpen(true); }}
        className="inline-flex items-center gap-1 text-[10px] text-primary hover:underline font-semibold uppercase tracking-widest"
        data-testid={testid}
        title="Generate AI subject-line variants"
      >
        <span aria-hidden>✨</span> AI variants
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => { if (!busy) setOpen(false); }}
          data-testid="subject-optimizer-modal"
        >
          <div
            className="bg-card border border-border max-w-2xl w-full max-h-[80vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-3 border-b border-border flex items-center justify-between">
              <div>
                <div className="font-semibold text-sm">AI subject-line optimizer</div>
                <div className="text-[11px] text-muted-foreground">
                  Base: <b className="text-foreground">{baseSubject || "(empty)"}</b>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  className="h-7 text-xs border border-border bg-background px-1"
                  data-testid="subject-optimizer-tone"
                >
                  {["friendly", "professional", "urgent", "playful", "curious"].map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <select
                  value={numVariants}
                  onChange={(e) => setNumVariants(Number(e.target.value))}
                  className="h-7 text-xs border border-border bg-background px-1"
                  data-testid="subject-optimizer-count"
                >
                  {[2, 4, 6, 8].map((n) => <option key={n} value={n}>{n} variants</option>)}
                </select>
                <Button size="sm" className="rounded-none h-7" onClick={run} disabled={busy}
                        data-testid="subject-optimizer-run">
                  {busy ? "Generating…" : "Generate"}
                </Button>
                <button type="button" onClick={() => setOpen(false)}
                        className="text-muted-foreground hover:text-foreground text-lg leading-none"
                        data-testid="subject-optimizer-close">×</button>
              </div>
            </div>
            <div className="p-3 space-y-1.5">
              {busy && (
                <div className="text-xs text-muted-foreground italic py-6 text-center">
                  Claude is drafting {numVariants} variants…
                </div>
              )}
              {!busy && variants.length === 0 && (
                <div className="text-xs text-muted-foreground italic py-6 text-center">
                  Click Generate to produce variants. Each variant includes an angle
                  and a coarse predicted open-rate delta vs your base subject.
                </div>
              )}
              {variants.map((v, i) => (
                <div key={i} className="border border-border p-2 flex items-start gap-2 hover:bg-muted/20"
                     data-testid={`subject-optimizer-variant-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate" title={v.subject}>
                      {v.subject}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-2">
                      <span className="uppercase tracking-widest">{v.angle}</span>
                      <span>·</span>
                      <span className={v.predicted_open_rate_delta_pct > 0 ? "text-emerald-700" : v.predicted_open_rate_delta_pct < 0 ? "text-red-600" : "text-muted-foreground"}>
                        {v.predicted_open_rate_delta_pct > 0 ? "+" : ""}{v.predicted_open_rate_delta_pct}% predicted
                      </span>
                    </div>
                  </div>
                  <Button
                    size="sm" variant="outline" className="rounded-none h-7"
                    onClick={() => { onPick?.(v.subject); setOpen(false); }}
                    data-testid={`subject-optimizer-pick-${i}`}
                  >Use</Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function CampaignEditor({ wsId, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [channel, setChannel] = useState("email");
  const [segId, setSegId] = useState("");
  const [tmplId, setTmplId] = useState("");
  const [provider, setProvider] = useState("resend");
  const [fromName, setFromName] = useState("Acquilix");
  const [fromEmail, setFromEmail] = useState("");
  const [fromNumber, setFromNumber] = useState("");
  const [dryRun, setDryRun] = useState(true);
  const [abEnabled, setAbEnabled] = useState(false);
  const [abTmplB, setAbTmplB] = useState("");
  const [abSplit, setAbSplit] = useState(50);
  const [abMetric, setAbMetric] = useState("opened");
  const [abAuto, setAbAuto] = useState(false);
  const [sto, setSto] = useState(false);
  const [segments, setSegments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(false);
  // Iter 194 — Interakt live templates
  const [interaktTemplates, setInteraktTemplates] = useState([]);
  const [interaktSource, setInteraktSource] = useState(null);
  const [interaktReason, setInteraktReason] = useState(null);
  const [interaktLoading, setInteraktLoading] = useState(false);
  const [pickedInterakt, setPickedInterakt] = useState(null);
  const [varMap, setVarMap] = useState({});
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/marketing/segments`).then((r) => setSegments(r.data || []));
    api.get(`/workspaces/${wsId}/marketing/templates?channel=${channel}`).then((r) => setTemplates(r.data || []));
  }, [wsId, channel]);
  // Fetch live Interakt catalog whenever we're on the WA+Interakt path
  useEffect(() => {
    if (channel !== "whatsapp" || provider !== "interakt") {
      setInteraktTemplates([]); setPickedInterakt(null); return;
    }
    setInteraktLoading(true);
    api.get(`/workspaces/${wsId}/marketing/wa-templates/interakt`)
      .then((r) => {
        setInteraktTemplates(r.data.rows || []);
        setInteraktSource(r.data.source || "mock");
        setInteraktReason(r.data.reason || null);
      })
      .catch(() => { setInteraktTemplates([]); })
      .finally(() => setInteraktLoading(false));
  }, [channel, provider, wsId]);
  const applyInterakt = (name) => {
    const t = interaktTemplates.find((x) => x.name === name);
    if (!t) { setPickedInterakt(null); return; }
    setPickedInterakt(t);
    const seed = {};
    (t.variables || []).forEach((v) => { seed[`{{${v.index}}}`] = v.example || ""; });
    setVarMap(seed);
  };
  const save = async () => {
    if (!name || !segId || !tmplId) { toast.error("Name, segment and template required"); return; }
    if (abEnabled && !abTmplB) { toast.error("Pick a Variant B template for the A/B test"); return; }
    if (abEnabled && abTmplB === tmplId) { toast.error("Variant B must be a different template"); return; }
    setBusy(true);
    try {
      await api.post(`/workspaces/${wsId}/marketing/campaigns`, {
        name, channel, segment_id: segId, template_id: tmplId, provider,
        from_name: fromName || null, from_email: fromEmail || null, from_number: fromNumber || null,
        dry_run: dryRun,
        send_time_optimization: sto,
        ab_test: abEnabled ? { enabled: true, variant_b_template_id: abTmplB, split_pct: Number(abSplit) || 50, winner_metric: abMetric, auto_promote: abAuto } : null,
        interakt_template: pickedInterakt ? {
          name: pickedInterakt.name,
          language: pickedInterakt.language,
          variables: varMap,
        } : null,
      });
      toast.success("Campaign created");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="campaign-editor">
        <DialogHeader><DialogTitle>New campaign</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="label-eyebrow mb-1.5 inline-block">Name *</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 rounded-none" data-testid="campaign-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-eyebrow mb-1.5 inline-block">Channel *</Label><Select value={channel} onValueChange={setChannel}><SelectTrigger className="h-9 rounded-none" data-testid="campaign-channel"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="email">Email</SelectItem><SelectItem value="whatsapp">WhatsApp</SelectItem></SelectContent></Select></div>
            <div><Label className="label-eyebrow mb-1.5 inline-block">Provider *</Label><Select value={provider} onValueChange={setProvider}><SelectTrigger className="h-9 rounded-none" data-testid="campaign-provider"><SelectValue /></SelectTrigger><SelectContent>{channel === "email" ? <SelectItem value="resend">Resend</SelectItem> : <><SelectItem value="twilio_wa">Twilio WA</SelectItem><SelectItem value="interakt">Interakt</SelectItem></>}</SelectContent></Select></div>
          </div>
          <div><Label className="label-eyebrow mb-1.5 inline-block">Segment *</Label><Select value={segId} onValueChange={setSegId}><SelectTrigger className="h-9 rounded-none" data-testid="campaign-segment"><SelectValue placeholder="Pick a segment" /></SelectTrigger><SelectContent>{segments.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.count})</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="label-eyebrow mb-1.5 inline-block">Template *</Label><Select value={tmplId} onValueChange={setTmplId}><SelectTrigger className="h-9 rounded-none" data-testid="campaign-template"><SelectValue placeholder="Pick a template" /></SelectTrigger><SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select></div>
          {channel === "email" ? (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-eyebrow mb-1.5 inline-block">From name</Label><Input value={fromName} onChange={(e) => setFromName(e.target.value)} className="h-9 rounded-none" data-testid="campaign-from-name" /></div>
              <div><Label className="label-eyebrow mb-1.5 inline-block">From email</Label><Input value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} placeholder="hello@yourdomain.com" className="h-9 rounded-none" data-testid="campaign-from-email" /></div>
            </div>
          ) : (
            <div><Label className="label-eyebrow mb-1.5 inline-block">From number (WA)</Label><Input value={fromNumber} onChange={(e) => setFromNumber(e.target.value)} placeholder="whatsapp:+14155238886" className="h-9 rounded-none" data-testid="campaign-from-number" /></div>
          )}
          {/* Iter 194 · Interakt live template picker */}
          {channel === "whatsapp" && provider === "interakt" && (
            <div className="border border-primary/40 p-3 space-y-2 bg-primary/5"
                    data-testid="campaign-interakt-picker">
              <div className="flex items-center justify-between">
                <Label className="label-eyebrow inline-block">
                  Interakt live template · {interaktSource === "mock" ? "MOCK" : "LIVE"}
                </Label>
                {interaktLoading && <span className="text-[10px] text-muted-foreground">Loading…</span>}
              </div>
              {interaktReason && (
                <div className="text-[10px] text-amber-800 bg-amber-50 border border-amber-300 px-2 py-1">
                  {interaktReason}
                </div>
              )}
              <Select value={pickedInterakt?.name || ""} onValueChange={applyInterakt}>
                <SelectTrigger className="h-9 rounded-none" data-testid="campaign-interakt-template">
                  <SelectValue placeholder="Pick a WhatsApp template" />
                </SelectTrigger>
                <SelectContent>
                  {interaktTemplates.map((t) => (
                    <SelectItem key={t.name} value={t.name}>
                      {t.name} · {t.language} · {t.category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {pickedInterakt && (
                <div className="space-y-2">
                  <div className="border border-border bg-white p-2 text-[11px] font-mono whitespace-pre-wrap max-h-40 overflow-y-auto"
                            data-testid="campaign-interakt-preview">
                    {pickedInterakt.body}
                  </div>
                  {(pickedInterakt.variables || []).length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-1">
                        Variable mapping
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {(pickedInterakt.variables || []).map((v) => (
                          <div key={v.index}>
                            <Label className="text-[10px]">{`{{${v.index}}}`}</Label>
                            <Input
                              value={varMap[`{{${v.index}}}`] || ""}
                              onChange={(e) => setVarMap({ ...varMap, [`{{${v.index}}}`]: e.target.value })}
                              className="h-8 rounded-none text-xs"
                              placeholder={v.example || `Value for {{${v.index}}}`}
                              data-testid={`campaign-interakt-var-${v.index}`} />
                          </div>
                        ))}
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-1">
                        Use <code>{"{{first_name}}"}</code> etc. from contact data — placeholders will be resolved on send.
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="border border-border p-3 space-y-2">
            <label className="flex items-center gap-2 text-xs cursor-pointer font-semibold"><input type="checkbox" checked={abEnabled} onChange={(e) => setAbEnabled(e.target.checked)} data-testid="campaign-ab-enabled" />A/B test two templates</label>
            {abEnabled && (
              <div className="space-y-2 pl-5">
                <div><Label className="label-eyebrow mb-1.5 inline-block">Variant B template *</Label><Select value={abTmplB} onValueChange={setAbTmplB}><SelectTrigger className="h-9 rounded-none" data-testid="campaign-ab-template"><SelectValue placeholder="Pick variant B" /></SelectTrigger><SelectContent>{templates.filter((t) => t.id !== tmplId).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="label-eyebrow mb-1.5 inline-block">Split % to A (10-90)</Label><Input type="number" min={10} max={90} value={abSplit} onChange={(e) => setAbSplit(e.target.value)} className="h-9 rounded-none" data-testid="campaign-ab-split" /></div>
                  <div><Label className="label-eyebrow mb-1.5 inline-block">Winner metric</Label><Select value={abMetric} onValueChange={setAbMetric}><SelectTrigger className="h-9 rounded-none" data-testid="campaign-ab-metric"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="opened">Open rate</SelectItem><SelectItem value="clicked">Click rate</SelectItem></SelectContent></Select></div>
                </div>
                <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={abAuto} onChange={(e) => setAbAuto(e.target.checked)} data-testid="campaign-ab-auto-promote" />Auto-promote winner — once a variant wins with 95% confidence (min 20 sends each), remaining queued sends switch to it</label>
              </div>
            )}
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={sto} onChange={(e) => setSto(e.target.checked)} data-testid="campaign-sto" />Send-time optimization (queue each contact at their historical best open hour)</label>
          <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} data-testid="campaign-dry-run" />Dry run (no real sends — mock delivery for testing)</label>
        </div>
        <DialogFooter><Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button><Button className="rounded-none" onClick={save} disabled={busy} data-testid="campaign-save">{busy ? "…" : "Create"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─────────────── AI Autopilot ───────────────
function ScrapeTab({ wsId }) {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get(`/workspaces/${wsId}/marketing/scrape-jobs`).then((r) => setRows(r.data));
  useEffect(() => { if (wsId) load(); /* eslint-disable-next-line */ }, [wsId]);
  const run = async (j) => {
    try {
      const r = await api.post(`/workspaces/${wsId}/marketing/scrape-jobs/${j.id}/run`);
      toast.success(`${r.data.imported} imported · ${r.data.updated || 0} updated`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };
  return (
    <div className="space-y-3" data-testid="scrape-tab">
      <Card className="rounded-none border-primary/30">
        <CardContent className="p-4 text-sm flex items-center gap-3">
          <Zap className="w-6 h-6 text-primary" />
          <div className="flex-1">
            <div className="font-bold">AI Prospecting Autopilot</div>
            <p className="text-xs text-foreground/60">Give Claude natural-language instructions once. It plans an Apollo query, runs it daily at 05:00 UTC, and imports fresh prospects into your CRM — ready for campaigns.</p>
          </div>
          <Button className="rounded-none" onClick={() => setEditing({})} data-testid="scrape-new"><Plus className="w-4 h-4 mr-1" />New job</Button>
        </CardContent>
      </Card>
      {!rows ? <Card className="rounded-none"><CardContent className="p-6 text-sm text-foreground/60">Loading…</CardContent></Card>
        : rows.length === 0 ? <Card className="rounded-none border-dashed"><CardContent className="p-10 text-center text-sm text-foreground/60" data-testid="scrape-empty">No autopilot jobs yet.</CardContent></Card>
        : (
          <div className="space-y-2" data-testid="scrape-list">
            {rows.map((j) => (
              <div key={j.id} className="border border-border p-4 flex items-center gap-3" data-testid={`scrape-row-${j.id}`}>
                <Sparkles className={`w-4 h-4 ${j.is_active ? "text-primary" : "text-foreground/30"}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{j.name}</div>
                  <div className="text-xs text-foreground/60 line-clamp-2 whitespace-pre-wrap">{j.instructions}</div>
                  <div className="text-[11px] text-foreground/55 mt-1 flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="rounded-none text-[10px]">{j.schedule}</Badge>
                    {j.last_run_at && <span>· last run {j.last_run_at.slice(0, 16).replace("T", " ")}</span>}
                    {j.last_run_imported != null && <span>· imported {j.last_run_imported}</span>}
                    {j.last_run_status && <Badge variant="outline" className={`rounded-none text-[10px] ${j.last_run_status === "ok" ? "border-emerald-500/40 text-emerald-700" : "border-amber-500/40 text-amber-700"}`}>{j.last_run_status}</Badge>}
                  </div>
                </div>
                <Button size="sm" className="rounded-none" onClick={() => run(j)} data-testid={`scrape-run-${j.id}`}><Play className="w-3 h-3 mr-1" />Run now</Button>
                <Button size="sm" variant="ghost" className="rounded-none" onClick={() => setEditing(j)} data-testid={`scrape-edit-${j.id}`}><Edit3 className="w-3 h-3" /></Button>
                <Button size="sm" variant="ghost" className="rounded-none text-destructive" onClick={async () => { if (window.confirm("Delete?")) { await api.delete(`/workspaces/${wsId}/marketing/scrape-jobs/${j.id}`); toast.success("Deleted"); load(); } }} data-testid={`scrape-delete-${j.id}`}><Trash2 className="w-3 h-3" /></Button>
              </div>
            ))}
          </div>
        )}
      {editing && <ScrapeEditor wsId={wsId} job={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
function ScrapeEditor({ wsId, job, onClose, onSaved }) {
  const isNew = !job?.id;
  const [name, setName] = useState(job?.name || "");
  const [instructions, setInstructions] = useState(job?.instructions || "");
  const [schedule, setSchedule] = useState(job?.schedule || "daily");
  const [isActive, setIsActive] = useState(job?.is_active ?? true);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim() || instructions.length < 5) { toast.error("Name + instructions required"); return; }
    setBusy(true);
    try {
      const payload = { name, instructions, schedule, sources: ["apollo"], is_active: isActive, default_page_size: 25 };
      if (isNew) await api.post(`/workspaces/${wsId}/marketing/scrape-jobs`, payload);
      else await api.patch(`/workspaces/${wsId}/marketing/scrape-jobs/${job.id}`, payload);
      toast.success(isNew ? "Job created" : "Updated");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="scrape-editor">
        <DialogHeader><DialogTitle>{isNew ? "New autopilot job" : `Edit ${job.name}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="label-eyebrow mb-1.5 inline-block">Name *</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 rounded-none" data-testid="scrape-name" /></div>
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Instructions (natural language)</Label>
            <Textarea rows={5} value={instructions} onChange={(e) => setInstructions(e.target.value)} className="rounded-none" placeholder="Find CEOs and Founders at SaaS companies in Bengaluru with 11-200 employees, focus on B2B startups founded after 2020" data-testid="scrape-instructions" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-eyebrow mb-1.5 inline-block">Schedule</Label><Select value={schedule} onValueChange={setSchedule}><SelectTrigger className="h-9 rounded-none" data-testid="scrape-schedule"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="daily">Daily (05:00 UTC)</SelectItem><SelectItem value="weekly">Weekly (Mon 05:00 UTC)</SelectItem><SelectItem value="manual">Manual only</SelectItem></SelectContent></Select></div>
            <label className="flex items-center gap-2 text-xs mt-6"><input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} data-testid="scrape-active" />Active</label>
          </div>
        </div>
        <DialogFooter><Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button><Button className="rounded-none" onClick={save} disabled={busy} data-testid="scrape-save">{busy ? "…" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─────────────── Sequences (Multi-step drip engine) ───────────────
function SequencesTab({ wsId, catalog }) {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [analyticsFor, setAnalyticsFor] = useState(null);
  const [enrollFor, setEnrollFor] = useState(null);
  const [aiSuggestOpen, setAiSuggestOpen] = useState(false);
  const [webhooksOpen, setWebhooksOpen] = useState(false);
  const load = () => api.get(`/workspaces/${wsId}/marketing/sequences`).then((r) => setRows(r.data));
  useEffect(() => { if (wsId) load(); /* eslint-disable-next-line */ }, [wsId]);
  return (
    <div className="space-y-3" data-testid="sequences-tab">
      <Card className="rounded-none border-primary/30">
        <CardContent className="p-4 text-sm flex items-center gap-3">
          <GitBranch className="w-6 h-6 text-primary" />
          <div className="flex-1">
            <div className="font-bold">Multi-step Sequences</div>
            <p className="text-xs text-foreground/60">Nurture flows with delays (hours/days), branching (opened / not opened / no reply / replied), across Email + WhatsApp. Engine sweeps every 5 minutes and reacts to real provider webhooks.</p>
          </div>
          <Button variant="outline" className="rounded-none" onClick={() => setWebhooksOpen(true)} data-testid="webhooks-setup"><Webhook className="w-4 h-4 mr-1" />Webhooks</Button>
          <Button variant="outline" className="rounded-none border-primary/40 text-primary" onClick={() => setAiSuggestOpen(true)} data-testid="sequence-ai-suggest"><Bot className="w-4 h-4 mr-1" />AI Suggest</Button>
          <Button className="rounded-none" onClick={() => setEditing({})} data-testid="sequence-new"><Plus className="w-4 h-4 mr-1" />New sequence</Button>
        </CardContent>
      </Card>
      {!rows ? <Card className="rounded-none"><CardContent className="p-6 text-sm text-foreground/60">Loading…</CardContent></Card>
        : rows.length === 0 ? <Card className="rounded-none border-dashed"><CardContent className="p-10 text-center text-sm text-foreground/60" data-testid="sequences-empty">No sequences yet — click &ldquo;New sequence&rdquo; to build your first drip flow.</CardContent></Card>
        : (
          <div className="space-y-2" data-testid="sequences-list">
            {rows.map((s) => (
              <div key={s.id} className="border border-border p-4 flex items-center gap-3" data-testid={`sequence-row-${s.id}`}>
                {s.channel_default === "email" ? <Mail className="w-4 h-4 text-primary" /> : <MessageCircle className="w-4 h-4 text-emerald-600" />}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{s.name}</div>
                  <div className="text-xs text-foreground/60 flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="rounded-none text-[10px]">{(s.steps || []).length} steps</Badge>
                    <Badge variant="outline" className="rounded-none text-[10px]">{s.channel_default}</Badge>
                    <Badge variant="outline" className={`rounded-none text-[10px] ${s.is_active ? "border-emerald-500/40 text-emerald-700" : "border-amber-500/40 text-amber-700"}`}>{s.is_active ? "active" : "paused"}</Badge>
                    <span>· active {s.stats?.active || 0}</span>
                    <span>· completed {s.stats?.completed || 0}</span>
                    <span>· stopped {s.stats?.stopped || 0}</span>
                  </div>
                </div>
                <Button size="sm" className="rounded-none" onClick={() => setEnrollFor(s)} data-testid={`sequence-enroll-${s.id}`}><Play className="w-3 h-3 mr-1" />Enroll</Button>
                <Button size="sm" variant="outline" className="rounded-none" onClick={() => setAnalyticsFor(s)} data-testid={`sequence-analytics-${s.id}`}><BarChart3 className="w-3 h-3" /></Button>
                <Button size="sm" variant="ghost" className="rounded-none" onClick={() => setEditing(s)} data-testid={`sequence-edit-${s.id}`}><Edit3 className="w-3 h-3" /></Button>
                <Button size="sm" variant="ghost" className="rounded-none text-amber-700" onClick={async () => { if (window.confirm(`Stop all active enrollments for "${s.name}"?`)) { await api.post(`/workspaces/${wsId}/marketing/sequences/${s.id}/stop-all`); toast.success("Stopped"); load(); } }} data-testid={`sequence-stopall-${s.id}`}><StopCircle className="w-3 h-3" /></Button>
                <Button size="sm" variant="ghost" className="rounded-none text-destructive" onClick={async () => { if (window.confirm(`Delete "${s.name}"?`)) { await api.delete(`/workspaces/${wsId}/marketing/sequences/${s.id}`); toast.success("Deleted"); load(); } }} data-testid={`sequence-delete-${s.id}`}><Trash2 className="w-3 h-3" /></Button>
              </div>
            ))}
          </div>
        )}
      {editing && <SequenceEditor wsId={wsId} seq={editing} catalog={catalog} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {analyticsFor && <SequenceAnalyticsDialog wsId={wsId} seq={analyticsFor} onClose={() => setAnalyticsFor(null)} />}
      {enrollFor && <SequenceEnrollDialog wsId={wsId} seq={enrollFor} onClose={() => setEnrollFor(null)} onDone={() => { setEnrollFor(null); load(); }} />}
      {aiSuggestOpen && <AiSuggestSequenceDialog wsId={wsId} onClose={() => setAiSuggestOpen(false)} onSaved={() => { setAiSuggestOpen(false); load(); }} />}
      {webhooksOpen && <WebhooksDialog wsId={wsId} onClose={() => setWebhooksOpen(false)} />}
    </div>
  );
}

function SequenceEditor({ wsId, seq, catalog, onClose, onSaved }) {
  const isNew = !seq?.id;
  const [name, setName] = useState(seq?.name || "");
  const [description, setDescription] = useState(seq?.description || "");
  const [channelDefault, setChannelDefault] = useState(seq?.channel_default || "email");
  const [providerDefault, setProviderDefault] = useState(seq?.provider_default || "resend");
  const [fromName, setFromName] = useState(seq?.from_name || "");
  const [fromEmail, setFromEmail] = useState(seq?.from_email || "");
  const [fromNumber, setFromNumber] = useState(seq?.from_number || "");
  const [isActive, setIsActive] = useState(seq?.is_active ?? true);
  const [steps, setSteps] = useState(seq?.steps?.length ? seq.steps : [{ template_id: "", delay_value: 0, delay_unit: "hours", branch_condition: "always", channel: null, provider: null }]);
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/marketing/templates`).then((r) => setTemplates(r.data || []));
  }, [wsId]);
  const addStep = () => setSteps([...steps, { template_id: "", delay_value: 24, delay_unit: "hours", branch_condition: "always", channel: null, provider: null }]);
  const removeStep = (i) => setSteps(steps.filter((_, idx) => idx !== i));
  const updateStep = (i, patch) => setSteps(steps.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const save = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    if (!steps.length || steps.some((s) => !s.template_id)) { toast.error("Every step needs a template"); return; }
    setBusy(true);
    try {
      const payload = {
        name, description: description || null,
        channel_default: channelDefault, provider_default: providerDefault,
        from_name: fromName || null, from_email: fromEmail || null, from_number: fromNumber || null,
        is_active: isActive,
        steps: steps.map((s) => ({
          template_id: s.template_id,
          channel: s.channel || null,
          delay_value: Number(s.delay_value) || 0,
          delay_unit: s.delay_unit || "hours",
          branch_condition: s.branch_condition || "always",
          provider: s.provider || null,
        })),
      };
      if (isNew) await api.post(`/workspaces/${wsId}/marketing/sequences`, payload);
      else await api.patch(`/workspaces/${wsId}/marketing/sequences/${seq.id}`, payload);
      toast.success(isNew ? "Sequence created" : "Sequence updated");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };
  const filteredTemplates = (channel) => templates.filter((t) => !channel || t.channel === channel);
  const branches = catalog?.branch_conditions || ["always", "no_reply", "opened", "not_opened"];
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="sequence-editor">
        <DialogHeader><DialogTitle>{isNew ? "New sequence" : `Edit ${seq.name}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-eyebrow mb-1.5 inline-block">Name *</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 rounded-none" data-testid="sequence-name" /></div>
            <div><Label className="label-eyebrow mb-1.5 inline-block">Default channel *</Label><Select value={channelDefault} onValueChange={setChannelDefault}><SelectTrigger className="h-9 rounded-none" data-testid="sequence-channel"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="email">Email</SelectItem><SelectItem value="whatsapp">WhatsApp</SelectItem></SelectContent></Select></div>
          </div>
          <div><Label className="label-eyebrow mb-1.5 inline-block">Description</Label><Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} className="rounded-none text-xs" placeholder="What is this sequence for?" data-testid="sequence-description" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-eyebrow mb-1.5 inline-block">Default provider *</Label><Select value={providerDefault} onValueChange={setProviderDefault}><SelectTrigger className="h-9 rounded-none" data-testid="sequence-provider"><SelectValue /></SelectTrigger><SelectContent>{channelDefault === "email" ? <SelectItem value="resend">Resend</SelectItem> : <><SelectItem value="twilio_wa">Twilio WA</SelectItem><SelectItem value="interakt">Interakt</SelectItem></>}</SelectContent></Select></div>
            <label className="flex items-center gap-2 text-xs mt-6"><input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} data-testid="sequence-active" />Active (enrollments run only when active)</label>
          </div>
          {channelDefault === "email" ? (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-eyebrow mb-1.5 inline-block">From name</Label><Input value={fromName} onChange={(e) => setFromName(e.target.value)} className="h-9 rounded-none" data-testid="sequence-from-name" placeholder="Acquilix" /></div>
              <div><Label className="label-eyebrow mb-1.5 inline-block">From email</Label><Input value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} className="h-9 rounded-none" data-testid="sequence-from-email" placeholder="hello@yourdomain.com" /></div>
            </div>
          ) : (
            <div><Label className="label-eyebrow mb-1.5 inline-block">From number (WA)</Label><Input value={fromNumber} onChange={(e) => setFromNumber(e.target.value)} className="h-9 rounded-none" data-testid="sequence-from-number" placeholder="whatsapp:+14155238886" /></div>
          )}
          <div className="pt-2 border-t border-border">
            <div className="flex items-center justify-between mb-2">
              <Label className="label-eyebrow inline-block">Steps · {steps.length}</Label>
              <Button size="sm" variant="outline" className="rounded-none h-7" onClick={addStep} data-testid="sequence-add-step"><Plus className="w-3 h-3 mr-1" />Add step</Button>
            </div>
            <div className="space-y-2" data-testid="sequence-steps">
              {steps.map((s, i) => (
                <div key={i} className="border border-border p-3 space-y-2 relative" data-testid={`sequence-step-${i}`}>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="rounded-none text-[10px]">Step {i + 1}</Badge>
                    {i === 0 && <span className="text-[10px] text-foreground/50">(fires when contact is enrolled)</span>}
                    <div className="flex-1" />
                    {steps.length > 1 && <Button size="sm" variant="ghost" className="rounded-none h-6 w-6 p-0 text-destructive" onClick={() => removeStep(i)} data-testid={`sequence-step-remove-${i}`}><Trash2 className="w-3 h-3" /></Button>}
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    <div>
                      <Label className="text-[10px] label-eyebrow inline-block mb-1"><Clock className="w-3 h-3 inline mr-1" />Wait</Label>
                      <Input type="number" min="0" value={s.delay_value} onChange={(e) => updateStep(i, { delay_value: e.target.value })} className="h-8 rounded-none text-xs" data-testid={`sequence-step-delay-value-${i}`} />
                    </div>
                    <div>
                      <Label className="text-[10px] label-eyebrow inline-block mb-1">Unit</Label>
                      <Select value={s.delay_unit} onValueChange={(v) => updateStep(i, { delay_unit: v })}><SelectTrigger className="h-8 rounded-none text-xs" data-testid={`sequence-step-delay-unit-${i}`}><SelectValue /></SelectTrigger><SelectContent><SelectItem value="hours">hours</SelectItem><SelectItem value="days">days</SelectItem></SelectContent></Select>
                    </div>
                    <div>
                      <Label className="text-[10px] label-eyebrow inline-block mb-1"><GitBranch className="w-3 h-3 inline mr-1" />Send if</Label>
                      <Select value={s.branch_condition} onValueChange={(v) => updateStep(i, { branch_condition: v })}><SelectTrigger className="h-8 rounded-none text-xs" data-testid={`sequence-step-branch-${i}`}><SelectValue /></SelectTrigger><SelectContent>{branches.map((b) => <SelectItem key={b} value={b}>{b.replace("_", " ")}</SelectItem>)}</SelectContent></Select>
                    </div>
                    <div>
                      <Label className="text-[10px] label-eyebrow inline-block mb-1">Channel override</Label>
                      <Select value={s.channel || "__inherit"} onValueChange={(v) => updateStep(i, { channel: v === "__inherit" ? null : v })}><SelectTrigger className="h-8 rounded-none text-xs" data-testid={`sequence-step-channel-${i}`}><SelectValue /></SelectTrigger><SelectContent><SelectItem value="__inherit">Inherit ({channelDefault})</SelectItem><SelectItem value="email">Email</SelectItem><SelectItem value="whatsapp">WhatsApp</SelectItem></SelectContent></Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] label-eyebrow inline-block mb-1">Template *</Label>
                    <Select value={s.template_id} onValueChange={(v) => updateStep(i, { template_id: v })}>
                      <SelectTrigger className="h-8 rounded-none text-xs" data-testid={`sequence-step-template-${i}`}><SelectValue placeholder="Pick a template" /></SelectTrigger>
                      <SelectContent>{filteredTemplates(s.channel || channelDefault).map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.channel})</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button><Button className="rounded-none" onClick={save} disabled={busy} data-testid="sequence-save">{busy ? "…" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SequenceEnrollDialog({ wsId, seq, onClose, onDone }) {
  const [segments, setSegments] = useState([]);
  const [segId, setSegId] = useState("");
  const [dryRun, setDryRun] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => {
    api.get(`/workspaces/${wsId}/marketing/segments`).then((r) => setSegments(r.data || []));
  }, [wsId]);
  const enroll = async () => {
    if (!segId) { toast.error("Pick a segment"); return; }
    setBusy(true); setResult(null);
    try {
      const r = await api.post(`/workspaces/${wsId}/marketing/sequences/${seq.id}/enroll`, {
        segment_id: segId, dry_run: dryRun,
      });
      setResult(r.data);
      toast.success(`${r.data.enrolled} enrolled${dryRun ? " (dry run)" : ""} · ${r.data.skipped} skipped`);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="sequence-enroll-dialog">
        <DialogHeader><DialogTitle>Enroll contacts into &ldquo;{seq.name}&rdquo;</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="label-eyebrow mb-1.5 inline-block">Segment *</Label><Select value={segId} onValueChange={setSegId}><SelectTrigger className="h-9 rounded-none" data-testid="enroll-segment"><SelectValue placeholder="Pick a segment" /></SelectTrigger><SelectContent>{segments.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.count})</SelectItem>)}</SelectContent></Select></div>
          <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} data-testid="enroll-dry-run" />Dry run (preview count only — no actual enrollments)</label>
          {result && (
            <Card className="rounded-none border-primary/30">
              <CardContent className="p-3 text-xs space-y-1" data-testid="enroll-result">
                <div><strong>{result.enrolled}</strong> contacts enrolled</div>
                <div><strong>{result.skipped}</strong> skipped (already active)</div>
                <div><strong>{result.matched_contacts}</strong> matched the segment filter</div>
              </CardContent>
            </Card>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Close</Button>
          <Button className="rounded-none" onClick={enroll} disabled={busy} data-testid="enroll-run"><Play className="w-3 h-3 mr-1" />{busy ? "Enrolling…" : "Enroll now"}</Button>
          {result && !dryRun && <Button variant="outline" className="rounded-none" onClick={onDone}>Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SequenceAnalyticsDialog({ wsId, seq, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/workspaces/${wsId}/marketing/sequences/${seq.id}/analytics`).then((r) => setData(r.data));
  }, [wsId, seq.id]);
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sequence-analytics-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><BarChart3 className="w-4 h-4" />Analytics · {seq.name}</DialogTitle></DialogHeader>
        {!data ? <div className="text-sm text-foreground/60">Loading…</div>
          : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2" data-testid="sequence-status-cards">
                <Card className="rounded-none"><CardContent className="p-3"><div className="text-[10px] label-eyebrow">Active</div><div className="text-2xl font-bold text-emerald-700">{data.status?.active || 0}</div></CardContent></Card>
                <Card className="rounded-none"><CardContent className="p-3"><div className="text-[10px] label-eyebrow">Completed</div><div className="text-2xl font-bold">{data.status?.completed || 0}</div></CardContent></Card>
                <Card className="rounded-none"><CardContent className="p-3"><div className="text-[10px] label-eyebrow">Stopped</div><div className="text-2xl font-bold text-amber-700">{data.status?.stopped || 0}</div></CardContent></Card>
              </div>
              <div>
                <Label className="label-eyebrow inline-block mb-2">Per-step funnel</Label>
                <div className="space-y-1" data-testid="sequence-step-funnel">
                  {data.steps.map((s) => {
                    const openRate = s.sent ? Math.round((s.opened / s.sent) * 100) : 0;
                    const clickRate = s.sent ? Math.round((s.clicked / s.sent) * 100) : 0;
                    return (
                      <div key={s.index} className="border border-border p-3 flex items-center gap-4 text-xs" data-testid={`sequence-analytics-step-${s.index}`}>
                        <Badge variant="outline" className="rounded-none">Step {s.index + 1}</Badge>
                        <span>sent <strong>{s.sent}</strong></span>
                        <span>opened <strong>{s.opened}</strong> ({openRate}%)</span>
                        <span>clicked <strong>{s.clicked}</strong> ({clickRate}%)</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        <DialogFooter><Button variant="outline" className="rounded-none" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────── AI Suggest Sequence Dialog ───────────────
function AiSuggestSequenceDialog({ wsId, onClose, onSaved }) {
  const [goal, setGoal] = useState("");
  const [audience, setAudience] = useState("");
  const [tone, setTone] = useState("professional");
  const [channel, setChannel] = useState("email");
  const [numSteps, setNumSteps] = useState(4);
  const [companyName, setCompanyName] = useState("");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [autoCreate, setAutoCreate] = useState(true);
  const [existingTemplates, setExistingTemplates] = useState([]);
  const [savedName, setSavedName] = useState("");

  useEffect(() => {
    api.get(`/workspaces/${wsId}/marketing/templates`).then((r) => setExistingTemplates(r.data || []));
  }, [wsId]);

  const generate = async () => {
    if (goal.trim().length < 3) { toast.error("Describe your goal"); return; }
    setBusy(true); setDraft(null);
    try {
      const r = await api.post(`/workspaces/${wsId}/marketing/ai/suggest-sequence`, {
        goal, audience: audience || null, tone, channel,
        num_steps: numSteps, company_name: companyName || null,
      });
      setDraft(r.data);
      setSavedName(r.data.name || `AI ${channel} sequence`);
    } catch (e) { toast.error(apiErrorMessage(e, "AI failed")); }
    finally { setBusy(false); }
  };

  const updateStep = (i, patch) => setDraft({ ...draft, steps: draft.steps.map((s, idx) => idx === i ? { ...s, ...patch } : s) });

  const save = async () => {
    if (!draft) return;
    if (!autoCreate && draft.steps.some((s) => !s.template_id)) { toast.error("Pick a template for each step"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/marketing/sequences/from-draft`, {
        name: savedName || draft.name,
        description: draft.description,
        channel_default: channel,
        provider_default: channel === "email" ? "resend" : "twilio_wa",
        is_active: true,
        auto_create_templates: autoCreate,
        steps: draft.steps,
      });
      const nT = r.data.templates_created?.length || 0;
      toast.success(`Sequence saved${nT ? ` + ${nT} templates created` : ""}`);
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="ai-suggest-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Bot className="w-4 h-4 text-primary" />AI Suggest Sequence</DialogTitle></DialogHeader>
        {!draft ? (
          <div className="space-y-3">
            <div><Label className="label-eyebrow mb-1.5 inline-block">Goal *</Label><Textarea rows={2} value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="Convert cold leads to demo bookings" className="rounded-none text-sm" data-testid="ai-suggest-goal" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-eyebrow mb-1.5 inline-block">Channel</Label><Select value={channel} onValueChange={setChannel}><SelectTrigger className="h-9 rounded-none" data-testid="ai-suggest-channel"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="email">Email</SelectItem><SelectItem value="whatsapp">WhatsApp</SelectItem></SelectContent></Select></div>
              <div><Label className="label-eyebrow mb-1.5 inline-block"># of steps</Label><Select value={String(numSteps)} onValueChange={(v) => setNumSteps(Number(v))}><SelectTrigger className="h-9 rounded-none" data-testid="ai-suggest-num-steps"><SelectValue /></SelectTrigger><SelectContent>{[3,4,5,6,7].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-eyebrow mb-1.5 inline-block">Tone</Label><Select value={tone} onValueChange={setTone}><SelectTrigger className="h-9 rounded-none" data-testid="ai-suggest-tone"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="friendly">Friendly</SelectItem><SelectItem value="professional">Professional</SelectItem><SelectItem value="urgent">Urgent</SelectItem><SelectItem value="playful">Playful</SelectItem><SelectItem value="direct">Direct</SelectItem></SelectContent></Select></div>
              <div><Label className="label-eyebrow mb-1.5 inline-block">Sender company</Label><Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Acquilix" className="h-9 rounded-none" data-testid="ai-suggest-company" /></div>
            </div>
            <div><Label className="label-eyebrow mb-1.5 inline-block">Audience</Label><Input value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="SaaS founders in India, 11-50 employees" className="h-9 rounded-none" data-testid="ai-suggest-audience" /></div>
            <Button className="rounded-none w-full" onClick={generate} disabled={busy} data-testid="ai-suggest-generate"><Bot className="w-3 h-3 mr-1" />{busy ? `Designing your ${numSteps}-step sequence…` : `Design ${numSteps}-step sequence`}</Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="ai-suggest-draft">
            <div><Label className="label-eyebrow mb-1.5 inline-block">Sequence name</Label><Input value={savedName} onChange={(e) => setSavedName(e.target.value)} className="h-9 rounded-none" data-testid="ai-suggest-name" /></div>
            {draft.description && <div className="text-xs text-foreground/70 border-l-2 border-primary/40 pl-3 py-1 italic">{draft.description}</div>}
            <div className="border border-border p-3 bg-muted/30">
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={autoCreate} onChange={(e) => setAutoCreate(e.target.checked)} data-testid="ai-suggest-auto-create" />
                Auto-create templates from AI drafts <span className="text-foreground/50">(uncheck to reuse existing templates)</span>
              </label>
            </div>
            <div className="space-y-2" data-testid="ai-suggest-steps">
              {draft.steps.map((s, i) => (
                <Card key={i} className="rounded-none" data-testid={`ai-suggest-step-${i}`}>
                  <CardContent className="p-3 space-y-2 text-xs">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="rounded-none">Step {i + 1}</Badge>
                      <Badge variant="outline" className="rounded-none">{s.delay_value} {s.delay_unit}</Badge>
                      <Badge variant="outline" className="rounded-none">{s.branch_condition.replace("_", " ")}</Badge>
                      <span className="font-semibold flex-1 truncate">{s.name}</span>
                    </div>
                    {!autoCreate ? (
                      <div>
                        <Label className="text-[10px] label-eyebrow inline-block mb-1">Template *</Label>
                        <Select value={s.template_id || ""} onValueChange={(v) => updateStep(i, { template_id: v })}>
                          <SelectTrigger className="h-8 rounded-none text-xs" data-testid={`ai-suggest-step-template-${i}`}><SelectValue placeholder="Pick a template" /></SelectTrigger>
                          <SelectContent>{existingTemplates.filter((t) => t.channel === channel).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    ) : (
                      <>
                        {s.subject && <div className="border-b border-border pb-1"><span className="text-foreground/60">Subject:</span> <strong>{s.subject}</strong></div>}
                        {channel === "email"
                          ? <div className="whitespace-pre-wrap max-h-40 overflow-y-auto" dangerouslySetInnerHTML={{ __html: s.body }} />
                          : <div className="whitespace-pre-wrap max-h-40 overflow-y-auto">{s.body}</div>}
                      </>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="rounded-none" onClick={() => setDraft(null)} data-testid="ai-suggest-back"><RefreshCw className="w-3 h-3 mr-1" />Redo</Button>
              <Button className="rounded-none flex-1" onClick={save} disabled={busy} data-testid="ai-suggest-save">{busy ? "Saving…" : `Save as sequence${autoCreate ? " + create templates" : ""}`}</Button>
            </div>
          </div>
        )}
        <DialogFooter><Button variant="outline" className="rounded-none" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─────────────── Webhooks Setup Dialog ───────────────
function WebhooksDialog({ wsId, onClose }) {
  const [rows, setRows] = useState({});
  const [busy, setBusy] = useState("");
  const [revealed, setRevealed] = useState({});
  const providers = [
    { key: "resend", label: "Resend (Email)", hint: "In Resend dashboard → Webhooks → Add endpoint, paste the URL and secret below." },
    { key: "twilio_wa", label: "Twilio (WhatsApp/SMS)", hint: "Use Messaging → Services → Integration → Status Callback URL. Twilio uses the account auth_token as the signing secret — keep it in the api_key field of your integration." },
    { key: "interakt", label: "Interakt (WhatsApp)", hint: "In Interakt console → Developer Settings → Webhooks. Paste the URL and signing secret below." },
  ];
  const loadOne = async (p) => {
    const r = await api.get(`/workspaces/${wsId}/integrations/${p}/webhook`);
    setRows((old) => ({ ...old, [p]: r.data }));
  };
  useEffect(() => { providers.forEach((p) => loadOne(p.key)); /* eslint-disable-next-line */ }, [wsId]);
  const rotate = async (p) => {
    setBusy(p);
    try {
      const r = await api.post(`/workspaces/${wsId}/integrations/${p}/webhook-secret`);
      setRevealed((old) => ({ ...old, [p]: r.data.webhook_secret }));
      toast.success("New secret generated — copy it now, it won't be shown again");
      await loadOne(p);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(""); }
  };
  const copyText = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="webhooks-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Webhook className="w-4 h-4 text-primary" />Provider Webhooks</DialogTitle></DialogHeader>
        <p className="text-xs text-foreground/60">These webhooks power real-time delivery / read / reply / bounce tracking. Every payload is signature-verified. Copy the URL + secret into each provider&apos;s dashboard.</p>
        <div className="space-y-3">
          {providers.map((p) => {
            const info = rows[p.key];
            return (
              <Card key={p.key} className="rounded-none" data-testid={`webhook-card-${p.key}`}>
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="font-semibold text-sm flex-1">{p.label}</div>
                    {info?.secret_configured
                      ? <Badge variant="outline" className="rounded-none text-[10px] border-emerald-500/40 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Secret set</Badge>
                      : <Badge variant="outline" className="rounded-none text-[10px] border-amber-500/40 text-amber-700">No secret yet</Badge>}
                  </div>
                  <p className="text-[11px] text-foreground/60">{p.hint}</p>
                  {info?.webhook_url && (
                    <div className="flex items-center gap-2">
                      <Input readOnly value={info.webhook_url} className="h-8 rounded-none text-[11px] font-mono" data-testid={`webhook-url-${p.key}`} />
                      <Button size="sm" variant="outline" className="rounded-none" onClick={() => copyText(info.webhook_url)} data-testid={`webhook-copy-url-${p.key}`}><Copy className="w-3 h-3" /></Button>
                    </div>
                  )}
                  {revealed[p.key] && (
                    <div className="border border-amber-500/30 bg-amber-500/5 p-2 space-y-1">
                      <div className="text-[10px] label-eyebrow text-amber-700">New secret (shown once)</div>
                      <div className="flex items-center gap-2">
                        <Input readOnly value={revealed[p.key]} className="h-8 rounded-none text-[11px] font-mono" data-testid={`webhook-secret-${p.key}`} />
                        <Button size="sm" variant="outline" className="rounded-none" onClick={() => copyText(revealed[p.key])} data-testid={`webhook-copy-secret-${p.key}`}><Copy className="w-3 h-3" /></Button>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" className="rounded-none" disabled={busy === p.key} onClick={() => rotate(p.key)} data-testid={`webhook-rotate-${p.key}`}><RefreshCw className={`w-3 h-3 mr-1 ${busy === p.key ? "animate-spin" : ""}`} />{info?.secret_configured ? "Rotate secret" : "Generate secret"}</Button>
                    {info?.secret_rotated_at && <span className="text-[10px] text-foreground/50">rotated {new Date(info.secret_rotated_at).toLocaleString()}</span>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        <DialogFooter><Button variant="outline" className="rounded-none" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

